import {normalizeSourceFileId} from './utils/sourceFileSelection';

export const SOURCES_ITEM_DRAG_MIME = 'application/x-sources-item';
const SNIPPET_DRAG_MIME = 'application/x-snippet';
const SNIPPETS_DRAG_MIME = 'application/x-snippets';

function setDataSafe(dataTransfer, mime, value) {
    try {
        dataTransfer?.setData?.(mime, value);
        return true;
    } catch (_) {
        return false;
    }
}

function getDataSafe(dataTransfer, mime) {
    try {
        return dataTransfer?.getData?.(mime) || '';
    } catch (_) {
        return '';
    }
}

export function createSourceItemDragPayload({itemType, id, parentFolderId = null, name = ''}) {
    if (!itemType || !id) {
        return null;
    }

    return {
        itemType,
        id: String(id),
        parentFolderId: parentFolderId == null ? null : String(parentFolderId),
        name: String(name || ''),
    };
}

export function createSourceItemsDragPayload(entries = []) {
    const items = (entries || [])
        .map((entry) => createSourceItemDragPayload(entry))
        .filter(Boolean);

    if (!items.length) {
        return null;
    }

    return {items};
}

export function createSnippetPayloadFromSourceFile(file) {
    const fileId = normalizeSourceFileId(file?.id);
    if (!fileId) {
        return null;
    }

    const fileName = String(file?.name || 'Untitled file');

    return {
        localId: `source-file:${fileId}`,
        fileId,
        docId: fileId,
        docTitle: fileName,
        docUrl: file?.url || null,
        docSource: file?.source || null,
        docSourceType: 'source-file',
        content: fileName,
    };
}

export function createSnippetPayloadsFromSourceFiles(files = []) {
    const snippets = (files || [])
        .map((file) => createSnippetPayloadFromSourceFile(file))
        .filter(Boolean);

    if (!snippets.length) {
        return [];
    }

    const deduped = [];
    const seen = new Set();
    snippets.forEach((snippet) => {
        const key = snippet.localId || `${snippet.docId || ''}:${snippet.docTitle || ''}`;
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        deduped.push(snippet);
    });

    return deduped;
}

export function writeDragPayload(dataTransfer, payload, snippet = null) {
    if (!payload) {
        return false;
    }

    const payloadJson = JSON.stringify(payload);
    const wroteCustomMime = setDataSafe(dataTransfer, SOURCES_ITEM_DRAG_MIME, payloadJson);
    const wroteTextPlain = setDataSafe(dataTransfer, 'text/plain', payloadJson);

    const snippets = Array.isArray(snippet)
        ? snippet.filter(Boolean)
        : (snippet ? [snippet] : []);

    if (snippets.length) {
        // Keep single-snippet mime for backward compatibility with existing drop targets.
        setDataSafe(dataTransfer, SNIPPET_DRAG_MIME, JSON.stringify(snippets[0]));
        setDataSafe(dataTransfer, SNIPPETS_DRAG_MIME, JSON.stringify(snippets));
    }

    try {
        if (dataTransfer) {
            dataTransfer.effectAllowed = 'copyMove';
        }
    } catch (_) {
        // ignore
    }

    return wroteCustomMime || wroteTextPlain;
}

export function readSourceItemDragPayload(dataTransfer) {
    const customRaw = getDataSafe(dataTransfer, SOURCES_ITEM_DRAG_MIME);
    const fallbackRaw = customRaw || getDataSafe(dataTransfer, 'text/plain');
    if (!fallbackRaw) {
        return null;
    }

    try {
        const parsed = JSON.parse(fallbackRaw);
        if (Array.isArray(parsed?.items)) {
            const normalizedItems = parsed.items
                .map((item) => (
                    item?.itemType && item?.id
                        ? {
                            itemType: item.itemType,
                            id: String(item.id),
                            parentFolderId: item.parentFolderId == null ? null : String(item.parentFolderId),
                            name: item.name || '',
                        }
                        : null
                ))
                .filter(Boolean);

            return normalizedItems.length ? {items: normalizedItems} : null;
        }

        if (!parsed?.itemType || !parsed?.id) {
            return null;
        }

        const single = {
            itemType: parsed.itemType,
            id: String(parsed.id),
            parentFolderId: parsed.parentFolderId == null ? null : String(parsed.parentFolderId),
            name: parsed.name || '',
        };

        return {items: [single]};
    } catch (_) {
        return null;
    }
}
